import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, Sun, Moon as MoonIcon, Clock, Heart } from "lucide-react";
import { fetchStatsPage } from "../lib/api";
import Avatar from "./Avatar";
import RiceBallJarWidget from "./RiceBallJarWidget";

// Soft empty/0-day color (never red). Counts > 0 use warm/sage.
const TREND_BAR_BG = "bg-warm-300/40";
const TREND_BAR_FILL = (n) => {
    if (n === 0) return "bg-warm-300/40";
    if (n <= 2) return "bg-sage";
    if (n <= 5) return "bg-mustard";
    return "bg-terracotta";
};

// Shame spiral detector: returns true if the user is likely shame-checking.
// Signals:
//   - 3+ stats opens in the last 2 minutes
//   - 0 wins this week AND any open
// Tracks opens in localStorage as a small ring buffer.
const SHAME_KEY = "mochi-stats-opens";
const SHAME_WINDOW_MS = 2 * 60 * 1000;
const SHAME_OPENS_THRESHOLD = 3;

function recordOpen() {
    try {
        const now = Date.now();
        const raw = window.localStorage.getItem(SHAME_KEY);
        const arr = raw ? JSON.parse(raw) : [];
        const fresh = arr.filter((t) => now - t < SHAME_WINDOW_MS);
        fresh.push(now);
        window.localStorage.setItem(SHAME_KEY, JSON.stringify(fresh.slice(-10)));
        return fresh.length;
    } catch {
        return 1;
    }
}

function clearShameOpens() {
    try { window.localStorage.removeItem(SHAME_KEY); } catch {/* ignore */}
}

export default function StatsPage({
    open,
    onClose,
    avatarStyle = "feminine",
    avatarSkin = null,
    onAcceptTinyTask,    // (task) => void  — caller can mark a task done
}) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [shameMode, setShameMode] = useState(false);

    // Pick a low-activation task from caller's still_in_progress data
    // (the StatsPage doesn't load tasks itself — we delegate via prop).
    const [tinyTask, setTinyTask] = useState(null);

    useEffect(() => {
        if (!open) return;
        const opens = recordOpen();
        setLoading(true);
        let cancel = false;
        (async () => {
            try {
                const d = await fetchStatsPage();
                if (cancel) return;
                setData(d);
                // Shame spiral conditions:
                //  (a) 3+ opens in 2 minutes
                //  (b) 0 wins this week AND any open
                const shame = opens >= SHAME_OPENS_THRESHOLD ||
                    (d.wins_this_week === 0 && d.still_in_progress >= 3);
                setShameMode(shame);
                // Try to surface a tiny task from /api/tasks for the soft handoff
                try {
                    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tasks`);
                    const tasks = await r.json();
                    const open = tasks.filter((t) => !t.completed);
                    if (open.length) {
                        const pick = open[Math.floor(Math.random() * open.length)];
                        if (!cancel) setTinyTask(pick);
                    }
                } catch {/* ignore */}
            } finally {
                if (!cancel) setLoading(false);
            }
        })();
        return () => { cancel = true; };
    }, [open]);

    const maxCount = useMemo(() => {
        if (!data?.week_trend) return 1;
        return Math.max(1, ...data.week_trend.map((d) => d.count));
    }, [data]);

    if (!open) return null;

    const dismissShame = () => {
        setShameMode(false);
        clearShameOpens();
    };

    const acceptTiny = () => {
        if (tinyTask && onAcceptTinyTask) onAcceptTinyTask(tinyTask);
        dismissShame();
        onClose?.();
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    className="fixed inset-0 z-[80] flex items-start justify-center bg-warm-900/30 backdrop-blur-sm overflow-y-auto"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                    data-testid="stats-page"
                >
                    <motion.div
                        initial={{ y: 24, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 24, opacity: 0 }}
                        transition={{ type: "spring", stiffness: 240, damping: 24 }}
                        className="relative w-full max-w-md min-h-[100dvh] sm:min-h-0 sm:my-8 bg-cream rounded-none sm:rounded-3xl p-5 shadow-clay"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-2">
                            <h2 className="font-display text-2xl text-warm-900 flex items-center gap-2">
                                <Sparkles size={18} className="text-mustard" />
                                Your week
                            </h2>
                            <button
                                onClick={onClose}
                                className="text-warm-500 p-1.5 rounded-full hover:bg-surface"
                                data-testid="stats-close"
                                aria-label="Close stats"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        {/* SHAME MODE OVERLAY — replaces numbers entirely with Mochi check-in */}
                        {shameMode && (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="relative rounded-clay bg-gradient-to-br from-terracotta-soft/40 to-sage-soft/40 p-5 my-4"
                                data-testid="shame-spiral-mode"
                            >
                                <div className="flex items-start gap-3 mb-3">
                                    <Avatar mood="cozy" size={60} style={avatarStyle} skin={avatarSkin} />
                                    <div className="flex-1">
                                        <p className="font-display text-lg text-warm-900 leading-snug">
                                            Hey. The numbers aren't the point right now.
                                        </p>
                                        <p className="font-body text-sm text-warm-500 mt-1">
                                            Want to just do one small thing together? 🍡
                                        </p>
                                    </div>
                                </div>
                                {tinyTask ? (
                                    <div className="bg-cream/80 rounded-2xl p-3 mb-3">
                                        <p className="font-body text-[11px] uppercase tracking-widest text-warm-500 mb-1">A tiny one:</p>
                                        <p className="font-display text-base text-warm-900">{tinyTask.title}</p>
                                    </div>
                                ) : (
                                    <div className="bg-cream/80 rounded-2xl p-3 mb-3 text-center">
                                        <p className="font-body text-sm text-warm-500 italic">No open tasks today — that's also fine 💛</p>
                                    </div>
                                )}
                                <div className="flex flex-col gap-2">
                                    {tinyTask && (
                                        <button
                                            onClick={acceptTiny}
                                            className="bg-terracotta hover:bg-terracotta-hover text-cream font-display text-sm py-2.5 rounded-full transition-all active:scale-95"
                                            data-testid="shame-accept-tiny"
                                        >
                                            Yes — let's do that one
                                        </button>
                                    )}
                                    <button
                                        onClick={dismissShame}
                                        className="bg-cream/60 hover:bg-cream/90 text-warm-800 font-display text-sm py-2.5 rounded-full transition-all active:scale-95"
                                        data-testid="shame-dismiss"
                                    >
                                        Show me the numbers anyway
                                    </button>
                                </div>
                            </motion.div>
                        )}

                        {/* Numbers — visually muted when shame mode is active until dismissed */}
                        <div className={shameMode ? "opacity-30 blur-[1px] pointer-events-none transition-all" : "transition-all"}>
                            {loading && (
                                <p className="text-center text-warm-500 font-body italic py-10">Adding up your wins…</p>
                            )}

                            {!loading && data && (
                                <>
                                    {/* WIN #1 — largest number */}
                                    <div className="text-center my-6">
                                        <p className="font-display text-[11px] uppercase tracking-widest text-warm-500">You finished</p>
                                        <p
                                            className="font-display text-7xl text-terracotta leading-none mt-1"
                                            data-testid="stats-wins-this-week"
                                        >
                                            {data.wins_this_week}
                                        </p>
                                        <p className="font-display text-warm-900 mt-1">
                                            {data.wins_this_week === 1 ? "thing this week" : "things this week"}
                                        </p>
                                    </div>

                                    {/* Rice ball jar — lifetime fed + progress to next tier */}
                                    <RiceBallJarWidget />

                                    {/* WIN #2 — best day */}
                                    {data.best_day && (
                                        <div className="bg-mustard-soft/40 rounded-clay p-4 mb-3 flex items-center gap-3" data-testid="stats-best-day">
                                            <div className="w-10 h-10 bg-mustard rounded-full flex items-center justify-center text-warm-900">
                                                <Sun size={18} />
                                            </div>
                                            <div>
                                                <p className="font-body text-xs text-warm-500">Your best day was</p>
                                                <p className="font-display text-lg text-warm-900">{data.best_day}</p>
                                            </div>
                                        </div>
                                    )}

                                    {/* WIN #3 — focus minutes */}
                                    {data.focus_minutes_week > 0 && (
                                        <div className="bg-sage-soft/40 rounded-clay p-4 mb-3 flex items-center gap-3" data-testid="stats-focus-minutes">
                                            <div className="w-10 h-10 bg-sage rounded-full flex items-center justify-center text-cream">
                                                <Clock size={18} />
                                            </div>
                                            <div>
                                                <p className="font-body text-xs text-warm-500">In focus mode</p>
                                                <p className="font-display text-lg text-warm-900">{data.focus_minutes_week} minutes</p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Streak — never shows broken, only active / hibernating */}
                                    {data.current_streak > 0 ? (
                                        <div className="bg-terracotta-soft/40 rounded-clay p-4 mb-3 flex items-center gap-3" data-testid="stats-streak-active">
                                            <div className="w-10 h-10 bg-terracotta rounded-full flex items-center justify-center text-cream text-lg">
                                                🔥
                                            </div>
                                            <div>
                                                <p className="font-body text-xs text-warm-500">Active streak</p>
                                                <p className="font-display text-lg text-warm-900">{data.current_streak} {data.current_streak === 1 ? "day" : "days"} in a row</p>
                                            </div>
                                        </div>
                                    ) : data.streak_status === "hibernating" && (
                                        <div className="bg-surface rounded-clay p-4 mb-3 flex items-center gap-3" data-testid="stats-streak-hibernating">
                                            <div className="w-10 h-10 bg-warm-300/40 rounded-full flex items-center justify-center text-warm-800">
                                                <MoonIcon size={18} />
                                            </div>
                                            <div>
                                                <p className="font-body text-xs text-warm-500">Streak is resting</p>
                                                <p className="font-display text-lg text-warm-900">Hibernating — picks up when you do</p>
                                            </div>
                                        </div>
                                    )}

                                    {/* WIN #4 — best window */}
                                    {data.best_window && (
                                        <div className="bg-cream rounded-clay p-4 mb-3 flex items-center gap-3 border border-warm-300/40" data-testid="stats-best-window">
                                            <div className="w-10 h-10 bg-mustard-soft/60 rounded-full flex items-center justify-center text-warm-900">
                                                <Heart size={16} />
                                            </div>
                                            <div>
                                                <p className="font-body text-xs text-warm-500">You tend to flow between</p>
                                                <p className="font-display text-lg text-warm-900">{data.best_window}</p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Weekly trend — left-to-right bars, never red */}
                                    <div className="mt-5">
                                        <p className="font-display text-[11px] uppercase tracking-widest text-warm-500 mb-2">This week</p>
                                        <div className="flex items-end gap-1.5 h-28" data-testid="stats-week-trend">
                                            {data.week_trend.map((d) => {
                                                const h = d.count === 0
                                                    ? 6
                                                    : Math.max(8, Math.round((d.count / maxCount) * 100));
                                                return (
                                                    <div key={d.day} className="flex-1 flex flex-col items-center justify-end gap-1">
                                                        <motion.div
                                                            className={`w-full rounded-t-md ${TREND_BAR_FILL(d.count)} ${d.is_today ? "ring-2 ring-terracotta/60" : ""}`}
                                                            style={{ height: `${h}%`, minHeight: 6 }}
                                                            initial={{ scaleY: 0, originY: 1 }}
                                                            animate={{ scaleY: 1 }}
                                                            transition={{ delay: 0.05 * data.week_trend.indexOf(d), type: "spring", stiffness: 240, damping: 18 }}
                                                            title={d.count === 0 ? "Rest day" : `${d.count} wins`}
                                                        />
                                                        <p className="text-[10px] font-display text-warm-500">{d.label}</p>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        <p className="text-[10px] font-body text-warm-500 mt-2 italic text-center">
                                            light bars are rest days — they count too 🍃
                                        </p>
                                    </div>
                                </>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
